import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { getApiErrorMessage } from "../lib/apiErrorMessage";
import { tenantScope } from "../lib/tenantScope";

import { TenantScopeSwitcher } from "./TenantScopeSwitcher";

/** A pending invitation never hides the selector for other active memberships. */
export function MembershipAccess() {
  const { t, i18n } = useTranslation();
  const { memberships, membershipStatus, refresh, signOut } = useAuth();
  const selected = memberships.find((m) => m.tenant_id === tenantScope());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await api.post("/v1/auth/memberships/accept", {
        tenant_id: selected.tenant_id,
        role: selected.role,
      });
      await refresh();
    } catch (cause) {
      setError(
        getApiErrorMessage(cause, i18n.language.startsWith("en") ? "en" : "fr") ||
          t("errors.generic"),
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <main className="grid min-h-dvh place-items-center bg-slate-50 px-4 py-8">
      <section className="w-full max-w-lg space-y-6 rounded-xl border border-slate-200 bg-white p-6">
        <TenantScopeSwitcher />
        <div>
          <h1 className="text-navy-950 text-xl font-semibold">{t("membership.title")}</h1>
          <p className="mt-2 text-sm text-slate-600">{t("membership.other_access")}</p>
        </div>
        {membershipStatus === "pending" && selected ? (
          <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="font-semibold text-slate-900">{selected.tenant_name}</p>
            <p className="text-sm text-slate-700">
              {t("membership.proposed_role", { role: t(`membership.role.${selected.role}`) })}
            </p>
            <p className="text-sm text-amber-900">{t("membership.status.pending")}</p>
            <button
              type="button"
              disabled={saving}
              onClick={() => void accept()}
              className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 min-h-11 rounded-lg px-4 py-2 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {saving ? t("app.loading") : t("membership.accept")}
            </button>
          </div>
        ) : (
          <p
            role="alert"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            {t("membership.unavailable")}
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          type="button"
          onClick={() => void signOut()}
          className="focus-visible:ring-brand-500 min-h-11 rounded-md px-3 text-sm text-slate-700 hover:bg-slate-100 focus-visible:ring-2"
        >
          {t("membership.sign_out")}
        </button>
      </section>
    </main>
  );
}
